<template>
    <v-hover v-slot:default="{ hover }">
        <v-card
                ref="propertyCard"
                :loading="isCallingApi"
                @click="$router.push('/property-details/' + property.id + '/' + propertySlug)"
                @mouseenter="$emit('hover', property.id)"
                @mouseleave="$emit('hover', null)"
                class="mx-auto property-card pa-0"
                max-width="100%"
                :elevation="(source === 'properties' && hover) || highlighted ? 16 : 3"
                :class="{ 'source-properties': source === 'properties', 'property-card--highlighted': highlighted }"
        >
            <span itemscope itemtype="https://schema.org/RentAction">
                <meta itemprop="name" :content="metaData.title">
                <meta itemprop="description" :content="metaData.description">
                <meta v-if="metaData.image" itemprop="image" :content="metaData.image">
                <meta itemprop="location" :content="metaData.location">
                <!-- <meta itemprop="price" :content="propertyUnit.price"> -->
                <meta itemprop="priceCurrency" :content="metaData.currency">
                <meta itemprop="url" :content="metaData.url">
            </span>

            <v-card-title class="pa-0">
                <div class="extra-info">
                    <v-chip label
                            class="rent-specials"
                            v-if="property.active_property_rent_specials && property.active_property_rent_specials.length > 0"
                    >
                        Rent Specials
                    </v-chip>
                    <v-chip label
                            class="sponsored"
                            v-if="property.is_sponsored"
                    >
                        Sponsored
                    </v-chip>
                </div>
                <v-btn fab
                    elevation="0"
                    class="favorite-icon"
                    v-if="source === 'properties'"
                    @click.stop="toggleLike()"
                    @mouseover="hoverLike = true"
                    @mouseleave="hoverLike = false"
                    width="34"
                    height="34"
                    color="rgba(255,255,255,0.5)"
                >
                    <v-icon
                            size="18"
                            :color="color"
                    >{{ favoriteIcon }}</v-icon>
                </v-btn>
                <div class="distance-from" v-if="distanceToSchool >= 0 && !isSharedHousing">
                    {{ distanceToSchool }} miles from school
                </div>
                <v-carousel
                        v-if="propertyImages.length"
                        :height="$vuetify.breakpoint.xs ? 160 : 200"
                        hide-delimiters
                        :show-arrows="false"
                        v-model="carouselIndex"
                    >
                    <div class="v-window__prev">
                        <v-btn icon aria-label="Previous Visual" @click.stop="carouselIndex--">
                            <v-icon>mdi-chevron-left</v-icon>
                        </v-btn>
                    </div>
                    <div class="v-window__next">
                        <v-btn icon aria-label="Next Visual" @click.stop="carouselIndex++">
                            <v-icon>mdi-chevron-right</v-icon>
                        </v-btn>
                    </div>
                    <v-carousel-item v-for="(propertyImage, i) in propertyImages" :key="i">
                        <v-img :src="propertyImage.image_url" cover height="100%"></v-img>
                    </v-carousel-item>
                </v-carousel>
                <div v-else class="image-placeholder" :style="{ height: ($vuetify.breakpoint.xs ? 160 : 200) + 'px' }">
                    <v-icon size="48" color="grey lighten-1">mdi-image-outline</v-icon>
                </div>
                <div v-if="schoolLogoUrl" class="school-logo-overlay">
                    <v-img
                        :src="schoolLogoUrl"
                        contain
                        width="48"
                        height="48"
                    >
                        <template v-slot:placeholder>
                            <v-icon size="24" color="grey lighten-1">mdi-school</v-icon>
                        </template>
                    </v-img>
                </div>
            </v-card-title>
            <router-link :to="'/property-details/' + property.id + '/' + propertySlug" class="">
                <v-card-text class="card-content">
                    <h2 class="property-name">{{ property.name }}</h2>
                    <div class="property-address">
                        {{ property.address_line_1 }}<br>
                        {{ property.city }}{{ property.state || property.state_id ? ', ' : '' }}{{ getStateDisplay() }}{{ property.zip_code ? ' ' + property.zip_code : '' }}
                    </div>
                    <span class="d-none">{{ property.country }}</span>
                    <div class="price-bed-bath-row">
                        <div class="price-row">{{ priceLabel }}</div>
                        <div class="bed-bath-row">
                            <v-img width="16" height="16" contain src="@/assets/icons/icon-bed.svg"></v-img>
                            <span>{{ numBedsLabel }} bd</span>
                            <span class="dot-separator">&middot;</span>
                            <v-img width="16" height="16" contain src="@/assets/icons/icon-bath.svg"></v-img>
                            <span>{{ numBathslabel }} ba</span>
                        </div>
                    </div>
                </v-card-text>
            </router-link>
        </v-card>
    </v-hover>
</template>

<script>
    import EngagementService from '@/services/engagement.service'

    export default {
        name: 'PropertyCard',
        props: {
            source: {
                type: String,
                default: () => ''
            },
            property: {
                type: Object,
                require: true
            },
            distanceToSchool: {
                type: [Number, String],
                default: () => -1
            },
            highlighted: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                carouselIndex: 0,
                isCallingApi: false,
                colors: [
                    'indigo',
                    'warning',
                    'pink darken-2',
                    'red lighten-1',
                    'deep-purple accent-4',
                ],
                metaData: {},
                hoverLike: false,
                /**
                 * please read comment on isUserFavorited method
                 */
                favoriteControlFlag: null,
                impressionTracked: false,
                observer: null,
            }
        },
        computed: {
            isSharedHousing() {
                return this.property.housing_type === 'shared';
            },
            schoolLogoUrl() {
                return this.isSharedHousing
                    && this.property.schools
                    && this.property.schools.length
                    && this.property.schools[0].school
                    ? this.property.schools[0].school.logo_url
                    : null;
            },
            propertyMetaData() {
                return {
                    'title': this.property.name + ' Student Rentals',
                    'description': this.property.description,
                    'image': this.propertyImages.image_url ? this.propertyImages.image_url : null,
                    'url': location.protocol + '//' + location.host + '/property-details/' + this.property.id + '/' + this.propertySlug,
                    'location':
                        this.property.address_line_1 + ', ' +
                        this.property.city + ', ' +
                        this.property.zip_code,
                    'currency': 'USD',
                }
            },
            isUserLoggedIn() {
                return this.$store.getters.isUserLoggedIn;
            },
            isUserFavorited() {
                /**
                 * This is here to prevent waiting for the server response to change the button styles
                 * I know this looks kind of hacky but if we don't return the flow before checking the propertyUnit variable
                 * the view will tie this method with all the calculations related to the propertyUnit variable, and the like button styles won't be updated
                 * untill Vue rerender all components in the view that are binded to the propertyUnit variable.
                 * So the logic here is, if this.favoriteControlFlag === 1 item is liked if this.favoriteControlFlag === 0 item is not liked
                 * if this.favoriteControlFlag !== 1 and  this.favoriteControlFlag !== 0 then check the propertyUnit variable and redo or do all
                 * the calculations.
                 */
                if (this.favoriteControlFlag === 1) {
                    return true;
                } else if (this.favoriteControlFlag === 0) {
                    return false;
                }
                let hasFavorites = false;
                for (let property_unit of this.property.property_units) {
                    if (property_unit.favorite_property_units && property_unit.favorite_property_units.length) {
                        hasFavorites = true;
                        continue;
                    }
                }
                return hasFavorites;
            },
            favoriteIcon() {
                return (this.isUserFavorited || this.hoverLike) ? 'mdi-heart' : 'mdi-heart-outline';
            },
            user() {
                return this.$store.getters.getUser;
            },
            propertyImages() {
                return this.property
                    && this.property.property_images
                    ? this.property.property_images
                    : []
            },
            currentDisplayedImage() {
                return this.propertyImages.length > 0
                    ? this.propertyImages[0]
                    : null
            },
            color() {
                return this.isUserFavorited
                    || this.hoverLike
                    ? '#FF4141'
                    : 'rgba(0,0,0,0.75)'
            },
            propertySlug() {
                return this.property.slug;
            },
            propertyUnits() {
                if (this.property && this.property.property_units) {
                    const propertyUnits = this.property.property_units.slice();
                    propertyUnits.sort((a, b) => {
                        return a.price - b.price;
                    });
                    return propertyUnits;
                }
                return [];
            },
            priceLabel() {
                const fmt = (n) => Math.ceil(n).toLocaleString();
                if (this.property.min_price === this.property.max_price) {
                    return `$${fmt(this.property.min_price)}/mo`;
                }
                return `$${fmt(this.property.min_price)} - $${fmt(this.property.max_price)}/mo`;
            },
            numBedsLabel() {
                const sorted = this.property.property_units.slice().sort((a, b) => a.number_of_bedrooms - b.number_of_bedrooms);
                const min = sorted[0].number_of_bedrooms;
                const max = sorted[sorted.length - 1].number_of_bedrooms;
                return min === max ? `${min}` : `${min}-${max}`;
            },
            numBathslabel() {
                const sorted = this.property.property_units.slice().sort((a, b) => a.number_of_baths - b.number_of_baths);
                const min = sorted[0].number_of_baths;
                const max = sorted[sorted.length - 1].number_of_baths;
                return min === max ? `${min}` : `${min}-${max}`;
            },
            minPrice() {
                return 800;
            },
            maxPrice() {
                return 2500;
            },
            minBeds() {
                return 1;
            },
            maxBeds() {
                return 3;
            },
            minBaths() {
                return 1;
            },
            maxBaths() {
                return 2;
            }
        },
        methods: {
            getStateDisplay() {
                // Handle both object (when relationship is loaded) and string (legacy)
                if (this.property.state) {
                    return typeof this.property.state === 'object'
                        ? this.property.state.abbrev
                        : this.property.state;
                }
                return '';
            },
            toggleLike: async function() {
                if (this.isUserLoggedIn) {
                    this.$store.dispatch('setSelectUnitToggleLikeModal', true);
                    this.$store.dispatch('setPropertyToLike', this.property);
                    return;
                    // /**
                    //  * This is here to prevent waiting for the server response to change the button styles
                    //  */
                    // this.favoriteControlFlag = this.isUserFavorited ? 0 : 1;
                    // this.isCallingApi = true;
                    // await PropertyService.togglePropertyLike(propertyId, {property_id: propertyId, user_id: this.user.id}).catch(() => {
                    //     /**
                    //      * This is here to revert the style changes in case the server returns something different than 200
                    //      */
                    //     this.favoriteControlFlag = null;
                    // });
                    // this.isCallingApi = false;

                    // this.$store.dispatch('getProperties');
                    // this.$store.dispatch('getFavoriteProperties');

                    // return true;
                }
                this.$store.dispatch('setSignUpModal', true)
            },
            setupImpressionTracking() {
                if (typeof IntersectionObserver === 'undefined') return;

                this.observer = new IntersectionObserver((entries) => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting && !this.impressionTracked && this.currentDisplayedImage) {
                            this.impressionTracked = true;
                            EngagementService.trackImpression(this.currentDisplayedImage.id);
                        }
                    });
                }, { threshold: 0.5 });

                if (this.$refs.propertyCard && this.$refs.propertyCard.$el) {
                    this.observer.observe(this.$refs.propertyCard.$el);
                }
            },
        },
        async beforeMount() {
            this.metaData = this.propertyMetaData;
        },
        mounted() {
            this.setupImpressionTracking();
        },
        beforeDestroy() {
            if (this.observer) {
                this.observer.disconnect();
            }
        },
    }
</script>

<style scoped>
    .v-application a {
        color: rgba(0, 0, 0, 0.87) !important;
    }

    .property-card {
        position: relative;
        border-radius: 10px !important;
        transition: margin 0.3s ease-in-out, box-shadow 0.3s ease-in-out, transform 0.3s ease-in-out;
    }

    .property-card--highlighted {
        transform: scale(1.02);
        box-shadow: 0 0 0 3px #1976d2, 0 8px 24px rgba(25, 118, 210, 0.3) !important;
    }

    .property-card .v-card__title {
        position: relative;
    }

    .property-card .v-carousel {
        border-radius: 0;
        border-top-left-radius: 10px;
        border-top-right-radius: 10px;
    }

    .property-card .v-carousel .v-window__next i,
    .property-card .v-carousel .v-window__prev i {
        font-size: 28px !important;
        color: #FFFFFF;
    }

    .favorite-icon {
        position: absolute;
        top: 10px;
        right: 10px;
        z-index: 1;
    }

    .extra-info {
        position: absolute;
        top: 10px;
        left: 10px;
        z-index: 1;
    }

    .extra-info .rent-specials {
        letter-spacing: 0;
        font-size: 12px;
        background: rgba(255,255,255,0.8) !important;
        margin-right: 10px;
    }

    .extra-info .sponsored {
        letter-spacing: 0;
        font-size: 12px;
        color: #FFF;
        background: rgba(0,0,0,0.8) !important;
    }

    .property-card.source-properties:hover {
        margin-top: -5px;
    }

    .property-card.mobile:hover {
        margin-top: 0px;
    }

    /* Image placeholder */
    .image-placeholder {
        background: #f5f5f5;
        display: flex;
        align-items: center;
        justify-content: center;
        border-top-left-radius: 10px;
        border-top-right-radius: 10px;
    }

    /* Card content area */
    .card-content {
        padding: 12px 14px !important;
    }

    /* Bed / Bath row */
    .bed-bath-row {
        display: inline-flex;
        align-items: center;
        gap: 3px;
        font-size: 12px;
        color: rgba(0, 0, 0, 0.6);
        white-space: nowrap;
    }

    .dot-separator {
        margin: 0 2px;
    }

    /* Property name — reserve 2 lines so cards align evenly */
    .property-name {
        font-size: 15px !important;
        font-weight: 600;
        line-height: 1.4;
        min-height: calc(2 * 1.4 * 15px);
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
    }

    /* Address */
    .property-address {
        font-size: 13px;
        line-height: 1.4;
        color: rgba(0, 0, 0, 0.5);
        margin-top: 2px;
    }

    /* Price + Bed/Bath row */
    .price-bed-bath-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 6px;
    }

    .price-row {
        font-size: 16px;
        font-weight: 800;
        color: #1a1a1a;
        line-height: 1.3;
        white-space: nowrap;
    }

    .property-card .distance-from {
        position: absolute;
        bottom: 10px;
        left: 10px;
        z-index: 1;
        font-size: 12px;
        line-height: 12px;
        color: white;
    }

    .school-logo-overlay {
        position: absolute;
        bottom: 12px;
        right: 12px;
        z-index: 1;
        width: 56px;
        height: 56px;
        background: rgba(255, 255, 255, 0.9);
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
        padding: 4px;
    }
</style>
